import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Save, Eye, Trash2, Send, EyeOff, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { templateOf, THEMES, outlineHtml } from "@/lib/templates";
import { AdminTitle } from "@/admin/AdminLayout";
import { ImageField } from "@/admin/ImageField";
import { RichEditor } from "@/admin/RichEditor";
import { InfoboxEditor, GalleryEditor, TreeEditor } from "@/admin/ListEditors";
import { useTitles } from "@/admin/LinkPickerDialog";
import { ReviewLinks, BatchReport, initialReview } from "@/admin/ReviewLinks";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY = {
  title: "", slug: "", section_id: "", summary: "", theme: "neutral", hero_image: "", hero_caption: "", hero_aspect: "panorama",
  drop_cap_image: "", drop_cap_replaces_letter: true, drop_cap_aspect: "square", drop_cap_size: "md", content: "",
  infobox_image: "", infobox_caption: "", infobox_aspect: "", infobox: [], gallery: [], tree: [], series_ids: [], book_ids: [], tags: [], published: false,
};

const CAP_SIZE_OPTS = [["sm", "Small"], ["md", "Medium"], ["lg", "Large"], ["xl", "Extra large"]];

const Card = ({ title, hint, children, testid }) => (
  <section data-testid={testid} className="vellum p-5 sm:p-6">
    <p className="font-heading font-bold text-[#2d1b0e]">{title}</p>
    {hint && <p className="text-sm italic text-[#5e4a35] mb-4">{hint}</p>}
    <div className={hint ? "" : "mt-4"}>{children}</div>
  </section>
);

export default function ArticleEditor({ mode = "admin" }) {
  const reader = mode === "reader";
  const base = reader ? "/contribute" : "/admin/articles";
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { sections, series, refresh } = useSite();
  const titles = useTitles();
  const [f, setF] = useState(id ? null : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const [aliases, setAliases] = useState("");
  const [review, setReview] = useState(null);
  const [reviewSt, setReviewSt] = useState(null);
  const [report, setReport] = useState(null);
  const load = useCallback(() => api.get(reader ? `/reader/articles/${id}` : `/admin/articles/${id}`).then((r) => setF({ ...EMPTY, ...r.data })).catch(() => toast.error("Article not found")), [id, reader]);

  useEffect(() => { if (id) load(); }, [id, load]);
  useEffect(() => {
    if (id && !reader) api.get("/admin/links/aliases").then((r) => setAliases(r.data.filter((a) => a.article_id === id).map((a) => a.alias).join("\n")));
  }, [id, reader]);

  useEffect(() => {
    if (id || !sections.length) return;
    const sec = reader ? sections.find((s) => s.is_readers) || sections[0] : sections.find((s) => s.slug === params.get("section")) || sections[0];
    setF((p) => (p.section_id ? p : { ...p, section_id: sec.id, theme: sec.theme, content: p.content || outlineHtml(templateOf(sec.template).outline), infobox: (sec.infobox_fields || []).map((label) => ({ label, value: "" })) }));
  }, [id, sections, params, reader]);

  if (!f) return <p className="font-display text-2xl p-10">Unrolling…</p>;
  const section = sections.find((s) => s.id === f.section_id);
  const tpl = section?.template || "generic";
  const locked = reader && f.review_status === "approved";

  const aliasList = () => aliases.split("\n").map((a) => a.trim()).filter(Boolean);
  const persist = async (overrides = {}, submit = false, go = true) => {
    if (!f.title.trim()) { toast.error("Give the article a title"); return null; }
    setSaving(true);
    try {
      const body = { ...f, ...overrides, expected_links_rev: f.links_rev || 0 };
      const cfg = reader ? { params: { submit } } : undefined;
      const path = reader ? "/reader/articles" : "/articles";
      const { data } = id ? await api.put(`${path}/${id}`, body, cfg) : await api.post(path, body, cfg);
      setF({ ...EMPTY, ...data });
      refresh();
      if (!reader) await api.put(`/admin/links/aliases/article/${data.id}`, { aliases: aliasList() });
      if (!id && go) navigate(`${base}/${data.id}`, { replace: true });
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setSaving(false); }
  };
  const saveDraft = async () => { if (await persist(reader ? {} : { published: false })) toast.success(f.published && !reader ? "Unpublished — saved as draft" : "Draft saved"); };
  const publish = async () => {
    if (!f.title.trim()) { toast.error("Give the article a title"); return; }
    setSaving(true);
    let proposals = [];
    try { proposals = (await api.post("/admin/links/scan-draft", { article_id: id || null, title: f.title, content: f.content, aliases: aliasList() })).data.proposals; }
    catch (e) { toast.error(`Link scan failed: ${errMsg(e)}`); } finally { setSaving(false); }
    if (!proposals.length) { if (await persist({ published: true })) toast.success("Published — now visible to readers"); return; }
    setReviewSt((prev) => initialReview(proposals, prev));
    setReview(proposals);
  };
  const confirmPublish = async (chosen, rejected) => {
    const d = await persist({ published: true }, false, false);
    if (!d) return;
    setReview(null);
    if (chosen.length) {
      try {
        const { data } = await api.post("/admin/links/apply", { proposals: chosen, rejected, draft_id: d.id });
        setReport(data);
      } catch (e) { setReport({ status: "failed", items: [], error: errMsg(e) }); }
    } else toast.success("Published. No links were added");
    setReviewSt(null);
    if (!id) navigate(`${base}/${d.id}`, { replace: true }); else load();
  };
  const submitReview = async () => { if (await persist({}, true)) toast.success("Sent to the Lorekeeper for approval"); };
  const preview = async () => {
    const w = window.open("", "_blank");
    const d = await persist(reader ? {} : {}, reader && f.review_status === "pending");
    if (d && w) w.location.href = `/preview/${d.id}`;
    else if (w) w.close();
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${f.title}" forever?`)) return;
    await api.delete(reader ? `/reader/articles/${id}` : `/articles/${id}`);
    toast.success("Article deleted");
    navigate(reader ? "/contribute" : "/admin/articles");
  };
  const toggleSeries = (sid) => set({ series_ids: f.series_ids.includes(sid) ? f.series_ids.filter((x) => x !== sid) : [...f.series_ids, sid] });
  const toggleBook = (b) => {
    const on = f.book_ids.includes(b.id);
    set({ book_ids: on ? f.book_ids.filter((x) => x !== b.id) : [...f.book_ids, b.id], series_ids: on || f.series_ids.includes(b.series_id) ? f.series_ids : [...f.series_ids, b.series_id] });
  };
  const statusText = reader
    ? { draft: "Draft — only you can see it", pending: "Awaiting the Lorekeeper's approval", rejected: "Returned by the Lorekeeper — edit and resubmit", approved: "Published in the Readers' Chronicles" }[f.review_status || "draft"]
    : f.published ? "Published — visible to readers" : "Draft — hidden from readers";

  return (
    <div data-testid="article-editor">
      <AdminTitle eyebrow={reader ? "Reader contribution" : id ? "Editing article" : "New article"} title={f.title || "Untitled page"}>
        {!reader && id && f.published && <Link to={`/wiki/${f.slug}`} target="_blank" data-testid="cms-view-article" className="btn-ghost-illum"><Eye className="w-4 h-4" /> View live</Link>}
        {!locked && <>
          <button onClick={saveDraft} disabled={saving} data-testid="cms-save-draft-button" className="btn-ghost-illum disabled:opacity-60">
            {!reader && f.published ? <><EyeOff className="w-4 h-4" /> Unpublish</> : <><Save className="w-4 h-4" /> Save draft</>}
          </button>
          <button onClick={preview} disabled={saving} data-testid="cms-preview-button" className="btn-ghost-illum disabled:opacity-60"><FileText className="w-4 h-4" /> Preview</button>
          {reader
            ? <button onClick={submitReview} disabled={saving} data-testid="cms-submit-review-button" className="btn-illum theme-flame disabled:opacity-60"><Send className="w-4 h-4" /> {f.review_status === "pending" ? "Update submission" : "Submit for approval"}</button>
            : <button onClick={publish} disabled={saving} data-testid="cms-save-article-button" className="btn-illum theme-flame disabled:opacity-60"><Send className="w-4 h-4" /> {saving ? "Saving…" : f.published ? "Update" : "Publish"}</button>}
        </>}
      </AdminTitle>
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent data-testid="review-links-dialog" className="bg-[#f4efe2] border-2 border-[#c9a227] rounded-none max-w-5xl w-[96vw] max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-display text-2xl">Review Links</DialogTitle>
          <DialogDescription className="italic">Only the first mention of each article gets a link. Tick the links you want, then publish.</DialogDescription>
          {review && reviewSt && <ReviewLinks proposals={review} st={reviewSt} set={setReviewSt} busy={saving} onBack={() => setReview(null)} onConfirm={confirmPublish} confirmLabel={f.published ? "Confirm & Update" : "Confirm & Publish"} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent data-testid="link-report-dialog" className="bg-[#f4efe2] border-2 border-[#c9a227] rounded-none max-w-2xl">
          <DialogTitle className="font-display text-2xl">Published: link report</DialogTitle>
          <DialogDescription className="italic">Every article that was changed is listed below.</DialogDescription>
          {report && (report.error ? <p data-testid="link-report-error" className="text-[#991b1b]">The article was published, but no links were added: {report.error}</p> : <BatchReport report={report} />)}
        </DialogContent>
      </Dialog>
      {locked && <p data-testid="editor-locked" className="vellum p-4 mb-6 inline-flex items-center gap-2"><Lock className="w-4 h-4" /> This article has been approved and published. Contact the Lorekeeper to change it.</p>}
      {reader && f.review_status === "rejected" && f.review_note && <p className="vellum p-4 mb-6 italic text-[#991b1b]">Lorekeeper's note: {f.review_note}</p>}

      <div className="grid xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-6">
          <section className="vellum p-5 sm:p-6 grid gap-4">
            <div>
              <label className="admin-label">Title</label>
              <input data-testid="article-title-input" value={f.title} onChange={(e) => set({ title: e.target.value })} className="admin-input !text-xl !font-heading" placeholder="e.g. Queen Elira of the Frostmarch" />
            </div>
            <div>
              <label className="admin-label">Summary (shown on cards, previews & under the title)</label>
              <textarea data-testid="article-summary-input" rows={2} value={f.summary} onChange={(e) => set({ summary: e.target.value })} className="admin-input" />
            </div>
          </section>
          <div>
            <p className="admin-label">Article text — select words and press “Link to page” to cross-link</p>
            <RichEditor key={id || "new"} value={f.content} onChange={(content) => set({ content })} />
          </div>
          <Card title="Infobox" hint="The fact panel on the right of the article. Values support [[Page Title]] links." testid="infobox-card">
            <div className="grid md:grid-cols-[14rem_1fr] gap-6">
              <div>
                <ImageField label={tpl === "house" ? "Sigil / banner" : tpl === "character" ? "Portrait" : "Infobox image"} value={f.infobox_image} onChange={(v) => set({ infobox_image: v })}
                  aspectKey={f.infobox_aspect} onAspect={(v) => set({ infobox_aspect: v })} aspectFallback={tpl === "house" || tpl === "character" ? "portrait" : "landscape"} testid="infobox-image-field" />
                <input value={f.infobox_caption} onChange={(e) => set({ infobox_caption: e.target.value })} placeholder="Image caption" className="admin-input mt-2" />
              </div>
              <InfoboxEditor fields={f.infobox} onChange={(infobox) => set({ infobox })} defaults={section?.infobox_fields?.length ? section.infobox_fields : templateOf(tpl).fields} />
            </div>
          </Card>
          {(tpl === "genealogy" || f.tree.length > 0) && (
            <Card title="Family tree" hint="Add each member and choose their parent. Link a member to their character page." testid="tree-card">
              <TreeEditor nodes={f.tree} onChange={(tree) => set({ tree })} titles={titles} />
            </Card>
          )}
          <Card title="Gallery" hint="Fixed image frames shown at the foot of the article." testid="gallery-card">
            <GalleryEditor items={f.gallery} onChange={(gallery) => set({ gallery })} />
          </Card>
        </div>

        <div className="xl:col-span-4 space-y-6">
          <Card title="Status" testid="publish-card">
            <p data-testid="article-status-text" className={`font-heading text-sm ${f.published ? "text-[#2f6b3a]" : "text-[#991b1b]"}`}>{statusText}</p>
            <label className="admin-label mt-5">Section</label>
            {reader ? <p className="admin-input">{section?.name || "Readers' Chronicles"}</p> : (
              <select data-testid="article-section-select" value={f.section_id} onChange={(e) => set({ section_id: e.target.value })} className="admin-input">
                {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <label className="admin-label mt-4">Colour theme</label>
            <div className="flex gap-2">
              {THEMES.map((t) => (
                <button key={t.value} type="button" data-testid={`article-theme-${t.value}`} onClick={() => set({ theme: t.value })}
                  className={`flex-1 py-2 font-heading text-xs border transition-colors ${f.theme === t.value ? "bg-[color:var(--accent)] text-white border-[#c9a227]" : "border-[#c9a227]/50 hover:bg-[#c9a227]/15"} theme-${t.value}`}>{t.label}</button>
              ))}
            </div>
            <label className="admin-label mt-4">URL slug</label>
            <input data-testid="article-slug-input" value={f.slug} onChange={(e) => set({ slug: e.target.value })} placeholder="auto from title" className="admin-input" />
            {!reader && <>
              <label className="admin-label mt-4">Also known as (aliases for linking)</label>
              <textarea data-testid="article-aliases-input" rows={2} value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="One per line, e.g. the Drakehelms" className="admin-input" />
            </>}
            <label className="admin-label mt-4">Tags (comma separated)</label>
            <input value={f.tags.join(", ")} onChange={(e) => set({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} className="admin-input" />
            {id && !locked && <button onClick={remove} data-testid="cms-delete-article" className="mt-6 inline-flex items-center gap-2 text-sm text-[#991b1b] hover:underline"><Trash2 className="w-4 h-4" /> Delete article</button>}
          </Card>
          <Card title="Drop cap" hint="Your illuminated initial — placed at the start of the first paragraph." testid="dropcap-card">
            <ImageField value={f.drop_cap_image} onChange={(v) => set({ drop_cap_image: v })} aspectKey={f.drop_cap_aspect} onAspect={(v) => set({ drop_cap_aspect: v })} aspectFallback="square" testid="dropcap-image-field" />
            <label className="admin-label mt-3">Drop cap size</label>
            <select data-testid="dropcap-size-select" value={f.drop_cap_size} onChange={(e) => set({ drop_cap_size: e.target.value })} className="admin-input">
              {CAP_SIZE_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <label className="flex items-center justify-between gap-3 mt-4">
              <span className="text-sm">Image replaces the first letter of the text</span>
              <input type="checkbox" data-testid="dropcap-replaces-switch" checked={f.drop_cap_replaces_letter} onChange={(e) => set({ drop_cap_replaces_letter: e.target.checked })} className="w-4 h-4 accent-[#991b1b]" />
            </label>
          </Card>
          <Card title="Hero banner" hint="Wide image at the very top of the page." testid="hero-card">
            <ImageField value={f.hero_image} onChange={(v) => set({ hero_image: v })} aspectKey={f.hero_aspect} onAspect={(v) => set({ hero_aspect: v })} aspectFallback="panorama" testid="hero-image-field" />
            <input value={f.hero_caption} onChange={(e) => set({ hero_caption: e.target.value })} placeholder="Banner caption" className="admin-input mt-2" />
          </Card>
          <Card title="Appears in" hint="Tick the books this page belongs to — readers can filter characters by book." testid="series-card">
            {series.map((s) => (
              <div key={s.id} className="mb-3">
                <label className="flex items-center gap-2 py-1 text-sm font-heading font-bold">
                  <input type="checkbox" data-testid={`article-series-${s.slug}`} checked={f.series_ids.includes(s.id)} onChange={() => toggleSeries(s.id)} className="accent-[#991b1b]" /> {s.title}
                </label>
                {s.books.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 py-0.5 pl-6 text-sm">
                    <input type="checkbox" data-testid={`article-book-${b.slug}`} checked={f.book_ids.includes(b.id)} onChange={() => toggleBook(b)} className="accent-[#1e4976]" /> Book {b.number}: {b.title}
                  </label>
                ))}
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
